import React, { useState } from 'react';
import { Calendar, DollarSign, AlertCircle, User, Edit2, Trash2, CheckCircle, Eye, MapPin, Target, Link as LinkIcon, FileText, TrendingUp, Zap } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import AdsRequestDetailModal from './AdsRequestDetailModal';

const AdsRequestList = ({ requests, onEdit, onDelete, onComplete, currentUser }) => {
  const [selectedRequest, setSelectedRequest] = useState(null);
  
  const getPriorityConfig = (prioridad) => {
    switch (prioridad) {
      case 'urgente':
        return {
          bg: 'from-red-500 to-red-600',
          badge: 'bg-red-100 text-red-800 border-red-300',
          glow: 'shadow-red-500/50',
          icon: '🔥'
        };
      case 'alta':
        return {
          bg: 'from-orange-500 to-orange-600',
          badge: 'bg-orange-100 text-orange-800 border-orange-300',
          glow: 'shadow-orange-500/50',
          icon: '⚡'
        };
      case 'normal':
        return {
          bg: 'from-blue-500 to-blue-600',
          badge: 'bg-blue-100 text-blue-800 border-blue-300',
          glow: 'shadow-blue-500/50',
          icon: '📌'
        };
      case 'baja':
        return {
          bg: 'from-gray-500 to-gray-600',
          badge: 'bg-gray-100 text-gray-800 border-gray-300',
          glow: 'shadow-gray-500/50',
          icon: '📋'
        };
      default:
        return {
          bg: 'from-gray-500 to-gray-600',
          badge: 'bg-gray-100 text-gray-800 border-gray-300',
          glow: 'shadow-gray-500/50',
          icon: '📋'
        };
    }
  };

  const getEstadoConfig = (estado) => {
    switch (estado) {
      case 'pendiente':
        return {
          bg: 'bg-gradient-to-r from-yellow-400 to-yellow-500',
          text: 'text-yellow-900',
          icon: '⏳'
        };
      case 'en_proceso':
        return {
          bg: 'bg-gradient-to-r from-blue-400 to-blue-500',
          text: 'text-blue-900',
          icon: '🔄'
        };
      case 'aprobado':
        return {
          bg: 'bg-gradient-to-r from-green-400 to-green-500',
          text: 'text-green-900',
          icon: '✅'
        };
      case 'rechazado':
        return {
          bg: 'bg-gradient-to-r from-red-400 to-red-500',
          text: 'text-red-900',
          icon: '❌'
        };
      case 'completado':
        return {
          bg: 'bg-gradient-to-r from-purple-400 to-purple-500',
          text: 'text-purple-900',
          icon: '🎉'
        };
      default:
        return {
          bg: 'bg-gradient-to-r from-gray-400 to-gray-500',
          text: 'text-gray-900',
          icon: '📄'
        };
    }
  };

  const getEstadoLabel = (estado) => {
    switch (estado) {
      case 'pendiente':
        return 'Pendiente';
      case 'en_proceso':
        return 'En Proceso';
      case 'aprobado':
        return 'Aprobado';
      case 'rechazado':
        return 'Rechazado';
      case 'completado':
        return 'Completado';
      default:
        return estado;
    }
  };

  const getTipoSolicitudLabel = (tipo) => {
    return tipo === 'nueva' ? 'Campaña Nueva' : 'Campaña Actual';
  };

  const getTipoSolicitudConfig = (tipo) => {
    return tipo === 'nueva' 
      ? { bg: 'bg-gradient-to-r from-emerald-400 to-emerald-500', text: 'text-emerald-900', icon: '🆕' }
      : { bg: 'bg-gradient-to-r from-cyan-400 to-cyan-500', text: 'text-cyan-900', icon: '🔄' };
  };

  const getPlatformConfig = (plataforma) => {
    return plataforma === 'meta'
      ? { bg: 'bg-gradient-to-r from-purple-500 to-indigo-600', icon: '📘', label: 'Meta Ads', glow: 'shadow-purple-500/30' }
      : { bg: 'bg-gradient-to-r from-blue-500 to-indigo-600', icon: '🔍', label: 'Google Ads', glow: 'shadow-blue-500/30' };
  };

  const formatFecha = (fecha) => {
    if (!fecha) return 'Sin fecha';
    try {
      return format(new Date(fecha), "d 'de' MMMM, yyyy", { locale: es });
    } catch (error) {
      return fecha;
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return '$0.00';
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN'
    }).format(amount);
  };

  if (!requests || requests.length === 0) {
    return (
      <div className="text-center py-16 bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl border-2 border-dashed border-gray-300">
        <div className="relative inline-block">
          <div className="absolute inset-0 bg-blue-400 rounded-full blur-xl opacity-20 animate-pulse"></div>
          <AlertCircle className="relative mx-auto h-16 w-16 text-gray-400" />
        </div>
        <h3 className="mt-4 text-lg font-bold text-gray-900">No hay solicitudes</h3>
        <p className="mt-2 text-sm text-gray-600 max-w-sm mx-auto">
          Comienza creando una nueva solicitud de anuncios para Google Ads o Meta Ads.
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {requests.map((request) => {
        const priorityConfig = getPriorityConfig(request.prioridad);
        const estadoConfig = getEstadoConfig(request.estado);
        const tipoConfig = getTipoSolicitudConfig(request.tipo_campana);
        const platformConfig = getPlatformConfig(request.plataforma);
        
        return (
          <div
            key={request.id}
            className={`group relative bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 overflow-hidden border border-gray-100`}
          >
            {/* Barra de prioridad superior con gradiente */}
            <div className={`h-2 bg-gradient-to-r ${priorityConfig.bg}`}></div>
            
            {/* Glow effect en hover */}
            <div className={`absolute inset-0 bg-gradient-to-br ${priorityConfig.bg} opacity-0 group-hover:opacity-5 transition-opacity duration-300 pointer-events-none`}></div>
            
            <div className="p-6 relative">
              {/* Header con badges premium */}
              <div className="mb-4">
                <div className="flex items-start justify-between mb-3">
                  <h3 className="text-xl font-bold text-gray-900 group-hover:text-blue-600 transition-colors flex-1 pr-2">
                    {request.nombre_cliente}
                  </h3>
                  <span className="text-2xl">{priorityConfig.icon}</span>
                </div>
                
                {/* Badges con diseño premium */}
                <div className="flex flex-wrap gap-2">
                  {/* Badge de Plataforma - Destacado */}
                  <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-bold text-white ${platformConfig.bg} shadow-lg ${platformConfig.glow} transform hover:scale-105 transition-transform`}>
                    <span className="mr-1">{platformConfig.icon}</span>
                    {platformConfig.label}
                  </span>
                  
                  {/* Badge de Tipo */}
                  <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-bold ${tipoConfig.bg} ${tipoConfig.text} shadow-md transform hover:scale-105 transition-transform`}>
                    <span className="mr-1">{tipoConfig.icon}</span>
                    {getTipoSolicitudLabel(request.tipo_campana)}
                  </span>
                  
                  {/* Badge de Prioridad */}
                  <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-bold border-2 ${priorityConfig.badge} shadow-md transform hover:scale-105 transition-transform`}>
                    <AlertCircle size={12} className="mr-1" />
                    {request.prioridad?.toUpperCase()}
                  </span>
                </div>
                
                {/* Badge de Estado - Separado y destacado */}
                <div className="mt-3">
                  <span className={`inline-flex items-center px-4 py-2 rounded-xl text-sm font-bold ${estadoConfig.bg} ${estadoConfig.text} shadow-lg transform hover:scale-105 transition-transform`}>
                    <span className="mr-2">{estadoConfig.icon}</span>
                    {getEstadoLabel(request.estado)}
                  </span>
                </div>
              </div>

              {/* Información Principal con diseño mejorado */}
              <div className="space-y-3 mb-4">
                {/* Nombre de Campaña */}
                {request.nombre_campana && (
                  <div className="flex items-start bg-gradient-to-r from-blue-50 to-indigo-50 p-3 rounded-xl border border-blue-100">
                    <Target size={18} className="mr-3 mt-0.5 text-blue-600 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-blue-600 uppercase tracking-wide mb-1">Campaña</p>
                      <p className="text-sm font-bold text-gray-900 truncate">{request.nombre_campana}</p>
                    </div>
                  </div>
                )}

                {/* Presupuesto Diario - Destacado */}
                {request.presupuesto_diario && (
                  <div className="relative overflow-hidden bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 p-4 rounded-xl border-2 border-green-200 shadow-md">
                    <div className="absolute top-0 right-0 w-20 h-20 bg-green-200 rounded-full blur-2xl opacity-30"></div>
                    <div className="relative flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="bg-green-500 p-2 rounded-lg mr-3">
                          <DollarSign size={20} className="text-white" />
                        </div>
                        <span className="text-sm font-semibold text-green-700">Presupuesto Diario</span>
                      </div>
                      <span className="text-2xl font-black text-green-600">
                        {formatCurrency(request.presupuesto_diario)}
                      </span>
                    </div>
                  </div>
                )}

                {/* Asignado a */}
                {request.asignado_a && (
                  <div className="flex items-center bg-gray-50 p-3 rounded-xl border border-gray-200">
                    <div className="bg-gradient-to-br from-purple-500 to-indigo-600 p-2 rounded-lg mr-3">
                      <User size={16} className="text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Asignado a</p>
                      <p className="text-sm font-bold text-gray-900 truncate">{request.asignado_a}</p>
                    </div>
                  </div>
                )}
              </div>

            {/* Botón Ver Detalles con diseño premium */}
            <button
              onClick={() => setSelectedRequest(request)}
              className="w-full flex items-center justify-center text-sm font-bold text-white py-3 border-2 border-transparent rounded-xl mb-4 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              <Eye size={18} className="mr-2" />
              Ver Información Completa
              <TrendingUp size={16} className="ml-2" />
            </button>

              {/* Acciones con diseño premium */}
              <div className="flex justify-end space-x-2 pt-4 border-t-2 border-gray-100">
                {request.estado !== 'completado' && request.estado !== 'aprobado' && (
                  <button
                    onClick={() => onComplete(request)}
                    className="group/btn p-3 text-green-600 hover:text-white bg-green-50 hover:bg-gradient-to-r hover:from-green-500 hover:to-green-600 rounded-xl transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-110"
                    title="Marcar como completado"
                  >
                    <CheckCircle size={20} className="group-hover/btn:animate-pulse" />
                  </button>
                )}
                <button
                  onClick={() => onEdit(request)}
                  className="group/btn p-3 text-blue-600 hover:text-white bg-blue-50 hover:bg-gradient-to-r hover:from-blue-500 hover:to-blue-600 rounded-xl transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-110"
                  title="Editar"
                >
                  <Edit2 size={20} className="group-hover/btn:animate-pulse" />
                </button>
                {currentUser && currentUser.role === 'admin' && (
                  <button
                    onClick={() => onDelete(request)}
                    className="group/btn p-3 text-red-600 hover:text-white bg-red-50 hover:bg-gradient-to-r hover:from-red-500 hover:to-red-600 rounded-xl transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-110"
                    title="Eliminar"
                  >
                    <Trash2 size={20} className="group-hover/btn:animate-pulse" />
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      })}
      
      {/* Modal de detalles */}
      {selectedRequest && (
        <AdsRequestDetailModal
          request={selectedRequest}
          onClose={() => setSelectedRequest(null)}
        />
      )}
    </div>
  );
};

export default AdsRequestList;
